import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Tag, Search, Eye, Trash2, FolderOpen, User, Calendar, AlertCircle, 
  Plus, Edit, Filter, X, CheckCircle, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import tagService from '../services/tagService';
import dataroomService from '../services/dataroomService';
import FilterBar from '../components/FilterBar';
import EnhancedDataTable from '../components/EnhancedDataTable';
import ResponsiveModal, { ResponsiveButton } from '../components/ResponsiveModal';
import { PageLoader } from '../components/CommonLoader';
import { CreateButton } from '../components/CommonButtons';

const SuperAdminTags = () => {
  const [tags, setTags] = useState([]);
  const [datarooms, setDatarooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagType, setSelectedTagType] = useState('');
  const [selectedDataroom, setSelectedDataroom] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'dataroom'
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    tag_type: 'file',
    dataroom_uuid: ''
  });

  useEffect(() => {
    fetchDatarooms();
  }, []);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllTags();
    } else if (selectedDataroom) {
      fetchDataroomTags();
    }
  }, [pagination.current_page, searchTerm, selectedTagType, selectedDataroom, viewMode]);

  const fetchDatarooms = async () => {
    try {
      const response = await dataroomService.getAllDatarooms({ per_page: 100 });
      setDatarooms(response.data || []);
    } catch (error) {
    }
  };

  const fetchAllTags = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTagType && { tag_type: selectedTagType })
      };

      const response = await tagService.getAllTags(params);
      
      if (response && response.tags && Array.isArray(response.tags)) {
        setTags(response.tags);
        
        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        setTags([]);
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const fetchDataroomTags = async () => {
    if (!selectedDataroom) return;
    
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTagType && { tag_type: selectedTagType })
      };

      const response = await tagService.getSuperAdminDataroomTags(selectedDataroom, params);
      
      if (response && response.tags && Array.isArray(response.tags)) {
        setTags(response.tags);
        
        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        setTags([]);
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      toast.error('Failed to load dataroom tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      tag_type: 'file',
      dataroom_uuid: selectedDataroom || ''
    });
    setShowCreateModal(true);
  };

  const handleEdit = async (tag) => {
    try {
      setLoading(true);
      let response;
      
      if (viewMode === 'dataroom' && selectedDataroom) {
        response = await tagService.getSuperAdminDataroomTag(selectedDataroom, tag.id);
      } else {
        response = await tagService.getSuperAdminTag(tag.id);
      }
      
      setSelectedTag(response.data);
      setFormData({
        name: response.data.name || '',
        tag_type: response.data.tag_type || 'file',
        dataroom_uuid: response.data.dataroom_uuid || selectedDataroom || ''
      });
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load tag details');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (tag) => {
    try {
      setLoading(true);
      let response;
      
      if (viewMode === 'dataroom' && selectedDataroom) {
        response = await tagService.getSuperAdminDataroomTag(selectedDataroom, tag.id);
      } else {
        response = await tagService.getSuperAdminTag(tag.id);
      }
      
      setSelectedTag(response.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to load tag details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (tag) => {
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const dataroomUuid = formData.dataroom_uuid || selectedDataroom;
      
      if (!dataroomUuid) {
        toast.error('Please select a dataroom');
        setIsSubmitting(false);
        return;
      }
      
      if (!formData.name.trim()) {
        toast.error('Tag name is required');
        setIsSubmitting(false);
        return;
      }
      
      const data = {
        name: formData.name.trim(),
        tag_type: formData.tag_type
      };
      
      await tagService.createSuperAdminDataroomTag(dataroomUuid, data);
      toast.success('Tag created successfully');
      setShowCreateModal(false);
      
      if (viewMode === 'all') {
        fetchAllTags();
      } else {
        fetchDataroomTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!formData.name.trim()) {
        toast.error('Tag name is required');
        setIsSubmitting(false);
        return;
      }
      
      const data = {
        name: formData.name.trim(),
        tag_type: formData.tag_type
      };
      
      const dataroomUuid = selectedTag.dataroom_uuid || selectedDataroom;
      
      await tagService.updateSuperAdminDataroomTag(dataroomUuid, selectedTag.id, data);
      toast.success('Tag updated successfully');
      setShowEditModal(false);
      
      if (viewMode === 'all') {
        fetchAllTags();
      } else {
        fetchDataroomTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    try {
      if (viewMode === 'dataroom' && selectedDataroom) {
        await tagService.deleteSuperAdminDataroomTag(selectedDataroom, selectedTag.id);
      } else if (selectedTag.dataroom_uuid) {
        await tagService.deleteSuperAdminDataroomTag(selectedTag.dataroom_uuid, selectedTag.id);
      } else {
        await tagService.deleteSuperAdminTag(selectedTag.id);
      }
      
      toast.success('Tag deleted successfully');
      setShowDeleteModal(false);
      
      if (viewMode === 'all') {
        fetchAllTags();
      } else {
        fetchDataroomTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Tag Name',
      sortable: true,
      render: (value, tag) => (
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{tag.name}</span>
        </div>
      )
    },
    {
      key: 'tag_type',
      header: 'Type',
      sortable: true,
      render: (value, tag) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          tag.tag_type === 'file' 
            ? 'bg-blue-100 text-blue-700' 
            : 'bg-green-100 text-green-700'
        }`}>
          {tag.tag_type}
        </span>
      )
    },
    {
      key: 'dataroom',
      header: 'Dataroom',
      render: (value, tag) => (
        <div className="flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-gray-400" />
          <span className="text-sm">{tag.dataroom?.name || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'created_by',
      header: 'Created By',
      render: (value, tag) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="text-sm">
            {tag.created_by?.first_name} {tag.created_by?.last_name}
          </span>
        </div>
      )
    },
    {
      key: 'admin',
      header: 'Admin',
      render: (value, tag) => (
        <span className="text-sm text-gray-600">
          {tag.dataroom?.admin?.first_name} {tag.dataroom?.admin?.last_name}
        </span>
      )
    },
    {
      key: 'created_at',
      header: 'Created Date',
      sortable: true,
      render: (value, tag) => new Date(tag.created_at).toLocaleDateString()
    }
  ];

  // Handle sorting
  const handleSort = (key, direction) => {
    const sorted = [...tags].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle nested values
      if (key === 'dataroom') {
        aVal = a.dataroom?.name;
        bVal = b.dataroom?.name;
      } else if (key === 'created_by') {
        aVal = `${a.created_by?.first_name} ${a.created_by?.last_name}`;
        bVal = `${b.created_by?.first_name} ${b.created_by?.last_name}`;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setTags(sorted);
  };

  const actions = (tag) => [
    {
      icon: Eye,
      label: 'View',
      onClick: () => handleView(tag),
      className: 'text-blue-600 hover:text-blue-800'
    },
    {
      icon: Edit,
      label: 'Edit',
      onClick: () => handleEdit(tag),
      className: 'text-green-600 hover:text-green-800'
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => handleDelete(tag),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  if (loading && tags.length === 0) {
    return <PageLoader />;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tags Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'all' 
              ? 'Manage all tags across the system' 
              : 'Manage tags for selected dataroom'}
          </p>
        </div>
        <button
          onClick={() => viewMode === 'all' ? fetchAllTags() : fetchDataroomTags()}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* View Mode Toggle */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setViewMode('all');
                setSelectedDataroom('');
                setPagination({ current_page: 1, per_page: 15, total: 0, last_page: 1 });
              }}
              className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Tags
            </button>
            <button
              onClick={() => {
                setViewMode('dataroom');
                setPagination({ current_page: 1, per_page: 15, total: 0, last_page: 1 });
              }}
              className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
                viewMode === 'dataroom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Dataroom Tags
            </button>
          </div>
          
          {viewMode === 'dataroom' && (
            <div className="flex-1">
              <select
                value={selectedDataroom}
                onChange={(e) => {
                  setSelectedDataroom(e.target.value);
                  setPagination({ current_page: 1, per_page: 15, total: 0, last_page: 1 });
                }}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Select a dataroom</option>
                {datarooms.map((dataroom) => (
                  <option key={dataroom.uuid} value={dataroom.uuid}>
                    {dataroom.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tags</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total}</p>
            </div>
            <Tag className="w-8 h-8 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">File Tags</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {tags.filter(t => t.tag_type === 'file').length}
              </p>
            </div>
            <Tag className="w-8 h-8 text-green-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Invitation Tags</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {tags.filter(t => t.tag_type === 'invitation').length}
              </p>
            </div>
            <Tag className="w-8 h-8 text-purple-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Datarooms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {viewMode === 'all' 
                  ? [...new Set(tags.map(t => t.dataroom?.uuid).filter(Boolean))].length
                  : selectedDataroom ? 1 : 0
                }
              </p>
            </div>
            <FolderOpen className="w-8 h-8 text-orange-500" />
          </div>
        </motion.div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search tags..."
        filters={[
          {
            label: "Tag Type",
            type: "select",
            value: selectedTagType,
            onChange: setSelectedTagType,
            options: [
              { value: "file", label: "File" },
              { value: "invitation", label: "Invitation" }
            ]
          }
        ]}
        onClearFilters={() => {
          setSearchTerm('');
          setSelectedTagType('');
        }}
      >
        <CreateButton onClick={handleCreate} size="sm">
          Create Tag
        </CreateButton>
      </FilterBar>

      {/* Tags Table */}
      {(viewMode === 'all' || selectedDataroom) && (
        <EnhancedDataTable
          columns={columns}
          data={tags}
          actions={actions}
          loading={loading}
          emptyMessage={
            viewMode === 'dataroom' && !selectedDataroom 
              ? "Please select a dataroom to view tags"
              : "No tags found"
          }
          pagination={pagination}
          onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
          itemName="tags"
          onSort={handleSort}
          defaultSortKey="created_at"
          defaultSortDirection="desc"
          showColumnControls={true}
          showRefresh={true}
          onRefresh={() => viewMode === 'all' ? fetchAllTags() : fetchDataroomTags()}
        />
      )}

      {/* Create Tag Modal */}
      <ResponsiveModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Tag"
      >
        <form onSubmit={handleSubmitCreate} className="space-y-4">
          {viewMode === 'all' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dataroom <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.dataroom_uuid}
                onChange={(e) => setFormData({ ...formData, dataroom_uuid: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              >
                <option value="">Select a dataroom</option>
                {datarooms.map((dataroom) => (
                  <option key={dataroom.uuid} value={dataroom.uuid}>
                    {dataroom.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="Enter tag name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => setFormData({ ...formData, tag_type: e.target.value })}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              required
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Create Tag
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* Edit Tag Modal */}
      <ResponsiveModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Tag"
      >
        <form onSubmit={handleSubmitEdit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="Enter tag name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => setFormData({ ...formData, tag_type: e.target.value })}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              required
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Update Tag
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* View Tag Modal */}
      <ResponsiveModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Tag Details"
      >
        {selectedTag && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">Tag Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTag.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Type</label>
                <p className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedTag.tag_type === 'file' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {selectedTag.tag_type}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Dataroom</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTag.dataroom?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Admin</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedTag.dataroom?.admin?.first_name} {selectedTag.dataroom?.admin?.last_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Created By</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedTag.created_by?.first_name} {selectedTag.created_by?.last_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Created Date</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedTag.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedTag.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end pt-4">
              <ResponsiveButton
                variant="secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </ResponsiveButton>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tag"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to delete the tag "{selectedTag?.name}"? This action cannot be undone.
            </p>
          </div>
          
          <div className="flex gap-3">
            <ResponsiveButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isSubmitting}
            >
              Delete Tag
            </ResponsiveButton>
            <ResponsiveButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default SuperAdminTags;